import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {useAuth} from '../modules/auth'

const resolveHomeRoute = (perfil?: string | number) => {
  const perfilNum = Number(perfil)
  if (perfilNum === 1) return '/home/admin'
  if (perfilNum === 2) return '/home/alumno'
  if (perfilNum === 3) return '/home/profesor'
  if (perfilNum === 5) return '/home/padre'
  if (perfilNum === 7) return '/home/multiperfil'
  return '/home/preceptor'
}

const HomeRedirect: FC = () => {
  const {currentUser} = useAuth()
  return <Navigate to={resolveHomeRoute(currentUser?.perfil)} />
}

const PrivateRoutes = () => {
  const SecurityProvidersPage = lazy(() => import('../pages/security-providers/SecurityProvidersPage'))
  const PreceptorHomePage = lazy(() => import('../pages/home/PreceptorHomePage'))
  const ProfesorHomePage = lazy(() => import('../pages/home/ProfesorHomePage'))
  const AlumnoHomePage = lazy(() => import('../pages/home/AlumnoHomePage'))
  const AdminHomePage = lazy(() => import('../pages/home/AdminHomePage'))
  const PadreHomePage = lazy(() => import('../pages/home/PadreHomePage'))
  const MultiperfilHomePage = lazy(() => import('../pages/home/MultiperfilHomePage'))
  const MiCursoAlumnoPage = lazy(() => import('../pages/cursos/MiCursoAlumnoPage'))
  const MisFaltasAlumnoPage = lazy(() => import('../pages/asistencias/MisFaltasAlumnoPage'))
  const NotasAlumnoPage = lazy(() => import('../pages/notas/NotasAlumnoPage'))
  const TpsAlumnoListaPage = lazy(() => import('../pages/tpsalumno/TpsAlumnoListaPage'))
  const MensajesListaPage = lazy(() => import('../pages/mensajes/MensajesListaPage'))
  const MensajeItemPage = lazy(() => import('../pages/mensajes/MensajeItemPage'))
  const MensajesRecibidosPage = lazy(() => import('../pages/mensajes/MensajesRecibidosPage'))
  const MensajesAuditoriaPage = lazy(() => import('../pages/mensajes/MensajesAuditoriaPage'))
  const ArchivosListaPage = lazy(() => import('../pages/archivos/ArchivosListaPage'))
  const ArchivoItemPage = lazy(() => import('../pages/archivos/ArchivoItemPage'))
  const TpsListaPage = lazy(() => import('../pages/tps/TpsListaPage'))
  const TpItemPage = lazy(() => import('../pages/tps/TpItemPage'))
  const TpRespuestasPage = lazy(() => import('../pages/tps/TpRespuestasPage'))
  const NotasListaPage = lazy(() => import('../pages/notas/NotasListaPage'))
  const NotaItemPage = lazy(() => import('../pages/notas/NotaItemPage'))
  const NotasAlumnosPage = lazy(() => import('../pages/notas/NotasAlumnosPage'))
  const NotasCursoPorMateriaPage = lazy(() => import('../pages/notas/NotasCursoPorMateriaPage'))
  const NotasCursoPorAlumnoPage = lazy(() => import('../pages/notas/NotasCursoPorAlumnoPage'))
  const AlumnosListaPage = lazy(() => import('../pages/alumnos/AlumnosListaPage'))
  const AlumnoItemPage = lazy(() => import('../pages/alumnos/AlumnoItemPage'))
  const AlumnoMensajesPage = lazy(() => import('../pages/alumnos/AlumnoMensajesPage'))
  const AlumnoCursosPage = lazy(() => import('../pages/alumnos/AlumnoCursosPage'))
  const AlumnoMateriasPage = lazy(() => import('../pages/alumnos/AlumnoMateriasPage'))
  const AlumnoInformesPage = lazy(() => import('../pages/alumnos/AlumnoInformesPage'))
  const AlumnoNotasRitePage = lazy(() => import('../pages/alumnos/AlumnoNotasRitePage'))
  const PadresListaPage = lazy(() => import('../pages/padres/PadresListaPage'))
  const PadreItemPage = lazy(() => import('../pages/padres/PadreItemPage'))
  const PadreMensajesPage = lazy(() => import('../pages/padres/PadreMensajesPage'))
  const ProfesoresListaPage = lazy(() => import('../pages/profesores/ProfesoresListaPage'))
  const ProfesorItemPage = lazy(() => import('../pages/profesores/ProfesorItemPage'))
  const ProfesoresDetallePage = lazy(() => import('../pages/profesores/ProfesoresDetallePage'))
  const ProfesorMensajesPage = lazy(() => import('../pages/profesores/ProfesorMensajesPage'))
  const MateriasListaPage = lazy(() => import('../pages/materias/MateriasListaPage'))
  const MateriaItemPage = lazy(() => import('../pages/materias/MateriaItemPage'))
  const CursosListaPage = lazy(() => import('../pages/cursos/CursosListaPage'))
  const CursoItemPage = lazy(() => import('../pages/cursos/CursoItemPage'))
  const AsistenciasListaPage = lazy(() => import('../pages/asistencias/AsistenciasListaPage'))
  const AsistenciasPersonalPage = lazy(() => import('../pages/asistencias/AsistenciasPersonalPage'))
  const AsistenciasPadrePage = lazy(() => import('../pages/asistencias/AsistenciasPadrePage'))
  const NotasPadrePage = lazy(() => import('../pages/notas/NotasPadrePage'))
  const VacantesListaPage = lazy(() => import('../pages/vacantes/VacantesListaPage'))
  const VacanteItemPage = lazy(() => import('../pages/vacantes/VacanteItemPage'))
  const NoticiasListaPage = lazy(() => import('../pages/noticias/NoticiasListaPage'))
  const NoticiaItemPage = lazy(() => import('../pages/noticias/NoticiaItemPage'))
  const NotasV2ListaPage = lazy(() => import('../pages/notasv2/NotasV2ListaPage'))
  const NotasV2InstanciaPage = lazy(() => import('../pages/notasv2/NotasV2InstanciaPage'))
  const NotasV2CursosPage = lazy(() => import('../pages/notasv2/NotasV2CursosPage'))
  const NotasV2VerNotasPage = lazy(() => import('../pages/notasv2/NotasV2VerNotasPage'))
  const RiteProfesorPage = lazy(() => import('../pages/rite/RiteProfesorPage'))
  const RiteCursoMateriaPage = lazy(() => import('../pages/rite/RiteCursoMateriaPage'))
  const MisNotasRitePage = lazy(() => import('../pages/notasv2/MisNotasRitePage'))
  const NotasRitePadrePage = lazy(() => import('../pages/notas/NotasRitePadrePage'))
  const NotasRiteV2PadrePage = lazy(() => import('../pages/notasv2/NotasRiteV2PadrePage'))
  const EstadisticasAsistenciasPage = lazy(() => import('../pages/estadisticas/EstadisticasAsistenciasPage'))
  const EstadisticasAusentismoPage = lazy(() => import('../pages/estadisticas/EstadisticasAusentismoPage'))
  const EstadisticasAccesoPersonalPage = lazy(() => import('../pages/estadisticas/EstadisticasAccesoPersonalPage'))
  const EstadisticasAccesoPadresPage = lazy(() => import('../pages/estadisticas/EstadisticasAccesoPadresPage'))
  const EstadisticasAccesoAlumnosPage = lazy(() => import('../pages/estadisticas/EstadisticasAccesoAlumnosPage'))
  const EstadisticasCargaDocentePage = lazy(() => import('../pages/estadisticas/EstadisticasCargaDocentePage'))
  const MundialPage = lazy(() => import('../pages/mundial/MundialPage'))
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<HomeRedirect />} />

        <Route
          path='home/preceptor'
          element={
            <SuspensedView>
              <PreceptorHomePage />
            </SuspensedView>
          }
        />
        <Route
          path='home/profesor'
          element={
            <SuspensedView>
              <ProfesorHomePage />
            </SuspensedView>
          }
        />
        <Route
          path='home/alumno'
          element={
            <SuspensedView>
              <AlumnoHomePage />
            </SuspensedView>
          }
        />
        <Route
          path='home/admin'
          element={
            <SuspensedView>
              <AdminHomePage />
            </SuspensedView>
          }
        />
        <Route
          path='home/padre'
          element={
            <SuspensedView>
              <PadreHomePage />
            </SuspensedView>
          }
        />
        <Route
          path='home/multiperfil'
          element={
            <SuspensedView>
              <MultiperfilHomePage />
            </SuspensedView>
          }
        />

        {/* Alumno subpages */}
        <Route
          path='cursos/alumno'
          element={
            <SuspensedView>
              <MiCursoAlumnoPage />
            </SuspensedView>
          }
        />

        <Route
          path='asistencias/alumno'
          element={
            <SuspensedView>
              <MisFaltasAlumnoPage />
            </SuspensedView>
          }
        />

        <Route
          path='notas/alumno'
          element={
            <SuspensedView>
              <NotasAlumnoPage />
            </SuspensedView>
          }
        />

        <Route
          path='notasv2/alumno'
          element={
            <SuspensedView>
              <MisNotasRitePage />
            </SuspensedView>
          }
        />

        <Route
          path='notasv2/padre'
          element={
            <SuspensedView>
              <NotasRiteV2PadrePage />
            </SuspensedView>
          }
        />

        <Route
          path='tpsalumno/lista'
          element={
            <SuspensedView>
              <TpsAlumnoListaPage />
            </SuspensedView>
          }
        />

        {/* Mensajes */}
        <Route
          path='mensajes/lista'
          element={
            <SuspensedView>
              <MensajesListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='mensajes/item/:id'
          element={
            <SuspensedView>
              <MensajeItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='mensajes/archivo'
          element={
            <SuspensedView>
              <MensajesRecibidosPage />
            </SuspensedView>
          }
        />

        <Route
          path='mensajes/auditoria'
          element={
            <SuspensedView>
              <MensajesAuditoriaPage />
            </SuspensedView>
          }
        />

        {/* Archivos Profesor */}
        <Route
          path='archivos/lista'
          element={
            <SuspensedView>
              <ArchivosListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='archivos/item/:id'
          element={
            <SuspensedView>
              <ArchivoItemPage />
            </SuspensedView>
          }
        />

        {/* Trabajos Practicos Profesor */}
        <Route
          path='tps/lista'
          element={
            <SuspensedView>
              <TpsListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='tps/item/:id/respuestas'
          element={
            <SuspensedView>
              <TpRespuestasPage />
            </SuspensedView>
          }
        />
        <Route
          path='tps/item/:id'
          element={
            <SuspensedView>
              <TpItemPage />
            </SuspensedView>
          }
        />

        {/* Notas Profesor */}
        <Route
          path='notas/lista'
          element={
            <SuspensedView>
              <NotasListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='notas/item/:id/alumnos'
          element={
            <SuspensedView>
              <NotasAlumnosPage />
            </SuspensedView>
          }
        />
        <Route
          path='notas/item/:id'
          element={
            <SuspensedView>
              <NotaItemPage />
            </SuspensedView>
          }
        />

        {/* Alumnos Preceptor */}
        <Route
          path='alumnos/lista'
          element={
            <SuspensedView>
              <AlumnosListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/mensajes/:idAlumno'
          element={
            <SuspensedView>
              <AlumnoMensajesPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/item/:id'
          element={
            <SuspensedView>
              <AlumnoItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/cursos'
          element={
            <SuspensedView>
              <AlumnoCursosPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/materias'
          element={
            <SuspensedView>
              <AlumnoMateriasPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/informes'
          element={
            <SuspensedView>
              <AlumnoInformesPage />
            </SuspensedView>
          }
        />
        <Route
          path='alumnos/notasrite'
          element={
            <SuspensedView>
              <AlumnoNotasRitePage />
            </SuspensedView>
          }
        />

        {/* Padres Preceptor */}
        <Route
          path='padres/lista'
          element={
            <SuspensedView>
              <PadresListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='padres/item/:id'
          element={
            <SuspensedView>
              <PadreItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='padres/mensajes/:idPadre'
          element={
            <SuspensedView>
              <PadreMensajesPage />
            </SuspensedView>
          }
        />

        {/* Profesores (Personal) */}
        <Route
          path='profesores/lista'
          element={
            <SuspensedView>
              <ProfesoresListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='profesores/detalle/:id'
          element={
            <SuspensedView>
              <ProfesoresDetallePage />
            </SuspensedView>
          }
        />
        <Route
          path='profesores/mensajes/:idProfesor'
          element={
            <SuspensedView>
              <ProfesorMensajesPage />
            </SuspensedView>
          }
        />
        <Route
          path='profesores/item/:id'
          element={
            <SuspensedView>
              <ProfesorItemPage />
            </SuspensedView>
          }
        />

        {/* Cursos Admin */}
        <Route
          path='cursos/lista'
          element={
            <SuspensedView>
              <CursosListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='cursos/item/:id'
          element={
            <SuspensedView>
              <CursoItemPage />
            </SuspensedView>
          }
        />

        {/* Informes por curso (desde lista de cursos) */}
        <Route
          path='notas/curso/:id/alumnos'
          element={
            <SuspensedView>
              <NotasCursoPorAlumnoPage />
            </SuspensedView>
          }
        />
        <Route
          path='notas/curso/:id'
          element={
            <SuspensedView>
              <NotasCursoPorMateriaPage />
            </SuspensedView>
          }
        />

        {/* Materias */}
        <Route
          path='materias/lista'
          element={
            <SuspensedView>
              <MateriasListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='materias/item/:id'
          element={
            <SuspensedView>
              <MateriaItemPage />
            </SuspensedView>
          }
        />

        {/* Asistencias */}
        <Route
          path='asistencias/lista'
          element={
            <SuspensedView>
              <AsistenciasListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='asistencias/personal'
          element={
            <SuspensedView>
              <AsistenciasPersonalPage />
            </SuspensedView>
          }
        />
        <Route
          path='asistencias/padre'
          element={
            <SuspensedView>
              <AsistenciasPadrePage />
            </SuspensedView>
          }
        />

        {/* Notas Padre */}
        <Route
          path='notas/padre'
          element={
            <SuspensedView>
              <NotasPadrePage />
            </SuspensedView>
          }
        />
        <Route
          path='notas/padre/rite'
          element={
            <SuspensedView>
              <NotasRitePadrePage />
            </SuspensedView>
          }
        />

        {/* Vacantes */}
        <Route
          path='vacantes/lista'
          element={
            <SuspensedView>
              <VacantesListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='vacantes/item/:id'
          element={
            <SuspensedView>
              <VacanteItemPage />
            </SuspensedView>
          }
        />

        {/* Noticias Web */}
        <Route
          path='noticias/lista'
          element={
            <SuspensedView>
              <NoticiasListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='noticias/item/:id'
          element={
            <SuspensedView>
              <NoticiaItemPage />
            </SuspensedView>
          }
        />

        {/* RITE Profesor */}
        <Route
          path='rite/lista'
          element={
            <SuspensedView>
              <RiteProfesorPage />
            </SuspensedView>
          }
        />
        <Route
          path='rite/materia/:id'
          element={
            <SuspensedView>
              <RiteCursoMateriaPage />
            </SuspensedView>
          }
        />

        {/* NotasV2 (RITE) */}
        <Route
          path='notasv2/lista'
          element={
            <SuspensedView>
              <NotasV2ListaPage />
            </SuspensedView>
          }
        />
        <Route
          path='notasv2/instancia/:id'
          element={
            <SuspensedView>
              <NotasV2InstanciaPage />
            </SuspensedView>
          }
        />
        <Route
          path='notasv2/instancia/:id/cursos'
          element={
            <SuspensedView>
              <NotasV2CursosPage />
            </SuspensedView>
          }
        />
        <Route
          path='notasv2/instancia/:instanciaId/cursos/:cursoMateriaId'
          element={
            <SuspensedView>
              <NotasV2VerNotasPage />
            </SuspensedView>
          }
        />

        {/* Estadísticas */}
        <Route
          path='estadisticas/asistencias'
          element={
            <SuspensedView>
              <EstadisticasAsistenciasPage />
            </SuspensedView>
          }
        />
        <Route
          path='estadisticas/ausentismo'
          element={
            <SuspensedView>
              <EstadisticasAusentismoPage />
            </SuspensedView>
          }
        />
        <Route
          path='estadisticas/acceso-personal'
          element={
            <SuspensedView>
              <EstadisticasAccesoPersonalPage />
            </SuspensedView>
          }
        />
        <Route
          path='estadisticas/acceso-padres'
          element={
            <SuspensedView>
              <EstadisticasAccesoPadresPage />
            </SuspensedView>
          }
        />
        <Route
          path='estadisticas/acceso-alumnos'
          element={
            <SuspensedView>
              <EstadisticasAccesoAlumnosPage />
            </SuspensedView>
          }
        />
        <Route
          path='estadisticas/carga-docente'
          element={
            <SuspensedView>
              <EstadisticasCargaDocentePage />
            </SuspensedView>
          }
        />

        {/* Mundial 2026 (Admin only) */}
        <Route
          path='mundial/fixture'
          element={
            <SuspensedView>
              <MundialPage />
            </SuspensedView>
          }
        />

        {/* Security Providers */}
        <Route
          path='security-providers/*'
          element={
            <SuspensedView>
              <SecurityProvidersPage />
            </SuspensedView>
          }
        />
        <Route index element={<HomeRedirect />} />
        
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
